'use client';
import { getMultiColorScale, getSingleColorScale } from '@mnd-frontend/ui/publish/utils';
import * as colors from '@mnd-frontend/ui/shared/tokens';
import { color as d3color } from 'd3-color';
import { type ReactNode, useMemo } from 'react';
import { v1 as uuid } from 'uuid';

const validateColor = (color?: string) =>
  (color && color.match(/^#([\da-f]{3,4}){1,2}$/i) && color) || undefined;

const colorMap = colors as unknown as Record<string, string>;
const getThemeColor = (color?: string) => colorMap[color ?? ''] || validateColor(color);

const setColors = (items: { color: string }[], colorScale: (index: number) => string) =>
  items.forEach(
    (item, i: number) => (item.color = d3color(colorScale(i))?.formatHex() ?? '#000000'),
  );

type DataPoint = {
  groupId?: string;
  color?: string;
  dataKey?: string;
  name: string | ReactNode;
};
type ExtendedDataPoint = DataPoint & { color: string; id: string };

export default function useColorAndId<T extends DataPoint>(
  array: T[],
  groups: { id: string | number }[] = [],
): (T & ExtendedDataPoint)[] {
  return useMemo(() => {
    const byGroup: Record<string, ExtendedDataPoint[]> = groups.reduce(
      (acc, group) => ({
        ...acc,
        [group.id]: [],
      }),
      {},
    );
    const noGroup: ExtendedDataPoint[] = [];
    const intermediate = array.map(object => {
      const color = getThemeColor(object.color);
      const item = { ...object, color, id: `${object.dataKey || object.name}_${uuid()}` };
      if (!color) ((item.groupId && byGroup[item.groupId]) || noGroup).push(item as any);
      return item;
    });
    const colorGroups = groups
      .map(g => byGroup[g.id])
      .concat([noGroup])
      .filter(a => a.length);
    const groupCount = colorGroups.length;
    if (groupCount > 1 && groupCount < 6) {
      colorGroups.map((items, i) => setColors(items, getSingleColorScale(i, items.length)));
    } else {
      const all = colorGroups.flat();
      setColors(all, getMultiColorScale(all.length));
    }
    return intermediate;
  }, [array, groups]) as (T & ExtendedDataPoint)[];
}
